import { Signal } from '@crown/signal';
import { availableLocales } from '../catalogue';
import {
  type I18nContext,
  type I18nSource,
  resolveI18nContext,
} from '../resolve';
import { createTranslator, type Translator } from '../translator';
import {
  type CatalogueSet,
  DEFAULT_LOCALE,
  DEFAULT_USE_CASE,
  type Locale,
  type MessageArgs,
  type TextDirection,
  type UseCase,
} from '../types';

export interface ConfigureI18nOptions {
  readonly catalogues: CatalogueSet;
  /** From `resolveI18nContext`; `available` should be `availableLocales(catalogues)`. */
  readonly context: I18nContext;
  readonly timeZone?: string | undefined;
  readonly onMissing?: ((key: string, locale: Locale) => void) | undefined;
}

/** The person's current locale. Write through `setLocale`. */
export const locale = new Signal.State<Locale>(DEFAULT_LOCALE);
export const useCase = new Signal.State<UseCase>(DEFAULT_USE_CASE);

const catalogues = new Signal.State<CatalogueSet>({});
/** The context the app last resolved; the country lives only here. */
const resolved = new Signal.State<I18nContext | undefined>(undefined);
const settings = new Signal.State<
  Pick<ConfigureI18nOptions, 'timeZone' | 'onMissing'>
>({});
let configured = false;

/**
 * Re-computed when the locale, use case or catalogues change; every `t` call
 * inside an `effect` re-runs with it.
 */
export const translator = new Signal.Computed<Translator>(() =>
  createTranslator({
    locale: locale.get(),
    useCase: useCase.get(),
    catalogues: catalogues.get(),
    timeZone: settings.get().timeZone,
    onMissing: settings.get().onMissing,
  }),
);

export function configureI18n(options: ConfigureI18nOptions): void {
  catalogues.set(options.catalogues);
  resolved.set(options.context);
  settings.set({ timeZone: options.timeZone, onMissing: options.onMissing });
  locale.set(options.context.locale);
  useCase.set(options.context.useCase);
  configured = true;
}

/** Register more catalogues (a lazily loaded page, a remote) without changing the locale. */
export function addCatalogues(more: CatalogueSet): void {
  const merged: Record<string, Record<string, string>> = {};
  for (const set of [catalogues.get(), more]) {
    for (const [name, catalogue] of Object.entries(set))
      merged[name] = { ...merged[name], ...catalogue };
  }
  catalogues.set(merged);
}

export function isI18nConfigured(): boolean {
  return configured;
}

export function translate(
  key: string,
  args?: MessageArgs,
  fallback?: string,
): string;
export function translate(key: string, fallback: string): string;
export function translate(
  key: string,
  argsOrFallback?: MessageArgs | string,
  fallback?: string,
): string {
  const current = translator.get();
  return typeof argsOrFallback === 'string'
    ? current.translate(key, argsOrFallback)
    : current.translate(key, argsOrFallback, fallback);
}

export function setLocale(next: Locale): void {
  locale.set(next);
}

export function setUseCase(next: UseCase): void {
  useCase.set(next);
}

/** `resolveI18nContext` against the catalogues configured so far. */
export function resolveBrowserContext(
  input: Omit<
    Parameters<typeof resolveI18nContext>[0],
    'available' | 'requested'
  > & {
    readonly requested?: readonly string[] | undefined;
  },
  set: CatalogueSet = catalogues.get(),
): I18nContext {
  const remembered = rememberedLocale();
  const requested = [
    ...(remembered ? [remembered] : []),
    ...(input.requested ??
      (typeof navigator === 'undefined'
        ? []
        : [...(navigator.languages ?? [navigator.language])])),
  ];
  return resolveI18nContext({
    ...input,
    requested,
    available: availableLocales(set),
  });
}

/**
 * Puts the resolved wording on `<html>`: `lang` and `dir` for the browser,
 * `data-use-case` and `data-country` so the choice is visible in the
 * inspector, addressable from CSS, and assertable in a test.
 */
export function applyDocumentLocale(
  doc: Document | undefined = typeof document === 'undefined'
    ? undefined
    : document,
): void {
  if (!doc) return;
  const current = translator.get();
  const root = doc.documentElement;
  root.setAttribute('lang', current.locale);
  root.setAttribute('dir', current.dir);
  root.setAttribute('data-use-case', current.useCase);
  const country = resolved.get()?.countryCode;
  if (country) root.setAttribute('data-country', country);
  else root.removeAttribute('data-country');
}

/**
 * Where the running wording came from. A support call asking "why does this
 * say patient record?" is answered by reading this, not by guessing.
 */
export interface I18nSnapshot {
  readonly locale: Locale;
  readonly useCase: UseCase;
  /** Which setting decided the use case. */
  readonly useCaseFrom: I18nSource;
  readonly countryCode: string | undefined;
  readonly dir: TextDirection;
  /** The locale this browser remembers, if the person has chosen one. */
  readonly remembered: Locale | undefined;
  /** Locales the running app has catalogues for. */
  readonly available: readonly Locale[];
}

export function i18nSnapshot(): I18nSnapshot {
  const current = translator.get();
  return {
    locale: current.locale,
    useCase: current.useCase,
    useCaseFrom: resolved.get()?.useCaseFrom ?? 'default',
    countryCode: resolved.get()?.countryCode,
    dir: current.dir,
    remembered: rememberedLocale(),
    available: availableLocales(catalogues.get()),
  };
}

const REMEMBERED_KEY = 'crown.locale';

/** The locale this browser last chose, if storage is available. */
export function rememberedLocale(): Locale | undefined {
  try {
    const value = localStorage.getItem(REMEMBERED_KEY);
    return value && value.trim() !== '' ? value : undefined;
  } catch {
    return undefined;
  }
}

export function rememberLocale(next: Locale | null): void {
  try {
    if (next) localStorage.setItem(REMEMBERED_KEY, next);
    else localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    // storage is a convenience; a private window renders from the session
  }
}

/** Names for the languages Crown ships, so a browser without that locale's data still shows them. */
const SHIPPED_NAMES: Readonly<Record<string, string>> = {
  'en-GB': 'English (UK)',
  'en-US': 'English (US)',
  cy: 'Cymraeg',
  gd: 'Gàidhlig',
  ar: 'العربية',
};

/**
 * The human name of a locale in its own language ("Cymraeg", "Deutsch"):
 * the shipped name when there is one, else what the browser knows, else
 * the tag.
 */
export function localeDisplayName(tag: Locale): string {
  const shipped = SHIPPED_NAMES[tag];
  if (shipped) return shipped;
  try {
    const name = new Intl.DisplayNames([tag], { type: 'language' }).of(tag);
    return name && name !== tag ? name : tag;
  } catch {
    return tag;
  }
}

/** Every catalogue configured or added so far. */
export function registeredCatalogues(): CatalogueSet {
  return catalogues.get();
}
